import React, { forwardRef } from 'react';

type InputSelectImageProps = {
  accept?: string;
  multiple?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  listFileName?: string[];
};

export const InputSelectImage = forwardRef<HTMLInputElement, InputSelectImageProps>(
  ({ accept = 'image/jpeg,image/png,image/jpg', multiple = true, onChange, listFileName }, ref) => {
    return (
      <input
        ref={ref}
        type="file"
        hidden
        accept={accept}
        multiple={multiple}
        onChange={(e) => {
          const files = Array.from(e.target.files || []);

          const uploadNames = files.map((f) => f.name.toLowerCase());
          const existingNames = (listFileName || []).map((n) => n.toLowerCase());
          const dupWithExisting = uploadNames.filter((name) => existingNames.includes(name));

          const seen = new Set<string>();
          const dupWithinUpload: string[] = [];

          uploadNames.forEach((name) => {
            if (seen.has(name)) {
              dupWithinUpload.push(name);
            } else {
              seen.add(name);
            }
          });
          const filteredOccurrences: Record<string, number> = {};

          const allDups = [...dupWithExisting, ...dupWithinUpload];
          if (allDups.length > 0) {
            const duplicatedSet = new Set<string>();
            allDups.forEach((name) => duplicatedSet.add(name));
            const duplicated = Array.from(duplicatedSet);

            alert(
              `Duplicate file detected: ${duplicated.join(
                ', '
              )}\nWe will keep only the first occurrence and remove the rest.`
            );

            const filtered = files.filter((file) => {
              const name = file.name.toLowerCase();

              if (!duplicated.includes(name)) return true;

              if (existingNames.includes(name)) return false;

              const count = filteredOccurrences[name] || 0;
              filteredOccurrences[name] = count + 1;
              return count === 0;
            });

            const dataTransfer = new DataTransfer();
            filtered.forEach((f) => dataTransfer.items.add(f));
            e.target.files = dataTransfer.files;

            if (filtered.length === 0) {
              e.target.value = '';
              return;
            }
          }

          const allowedTypes = accept.split(',').map((t) => t.trim().toLowerCase());
          const isValid = files.every((file) => {
            return allowedTypes.some((type) => {
              if (type.startsWith('.')) {
                return file.name.toLowerCase().endsWith(type);
              }
              return file.type.toLowerCase() === type;
            });
          });

          if (!isValid) {
            const invalidFiles = files
              .filter(
                (file) =>
                  !allowedTypes.some((type) => {
                    if (type.startsWith('.')) {
                      return file.name.toLowerCase().endsWith(type);
                    }
                    return file.type.toLowerCase() === type;
                  })
              )
              .map((file) => file.name)
              .join(', ');

            alert(
              `Invalid file format.\nAllowed types: ${accept}.\nInvalid files: ${invalidFiles}`
            );

            e.target.value = '';
            return;
          }

          if (onChange) onChange(e);
        }}
      />
    );
  }
);

InputSelectImage.displayName = 'InputSelectImage';
